export const sanitizeString = (
  str: string,
  options?: {
    trim?: boolean;
    removeExtraSpaces?: boolean;
    escapeHtml?: boolean;
  },
): string => {
  const { trim = true, removeExtraSpaces = true, escapeHtml = false } = options || {};

  if (!str || typeof str !== 'string') return '';

  let sanitized = str;

  if (trim) {
    sanitized = sanitized.trim();
  }

  if (removeExtraSpaces) {
    sanitized = sanitized.replace(/\s+/g, ' ');
  }

  if (escapeHtml) {
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // Remove control characters (Unicode range 0x00‑0x1F and DEL)
  sanitized = sanitized.replace(/[\u0000-\u001F\u007F]/g, '');

  return sanitized;
};

export const sanitizeEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return '';
  // Trim, lower‑case and strip control characters
  return email.trim().toLowerCase().replace(/[\u0000-\u001F\u007F]/g, '');
};

// ==================== VALIDATION ====================

export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email || typeof email !== 'string')
    return { valid: false, error: 'Email é obrigatório' };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) return { valid: false, error: 'Endereço de email inválido' };
  return { valid: true };
};

export const validatePassword = (
  password: string,
  options?: {
    minLength?: number;
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireNumber?: boolean;
  },
): { valid: boolean; error?: string } => {
  if (!password || typeof password !== 'string')
    return { valid: false, error: 'Palavra-passe é obrigatória' };
  const {
    minLength = 8,
    requireUppercase = false,
    requireLowercase = false,
    requireNumber = false,
  } = options || {};

  if (password.length < minLength)
    return {
      valid: false,
      error: `A palavra-passe deve ter pelo menos ${minLength} caracteres`,
    };
  if (requireUppercase && !/[A-Z]/.test(password))
    return { valid: false, error: 'A palavra-passe deve conter pelo menos uma letra maiúscula' };
  if (requireLowercase && !/[a-z]/.test(password))
    return { valid: false, error: 'A palavra-passe deve conter pelo menos uma letra minúscula' };
  if (requireNumber && !/\d/.test(password))
    return { valid: false, error: 'A palavra-passe deve conter pelo menos um número' };
  return { valid: true };
};

export const validateName = (
  name: string,
  label = 'Nome',
): { valid: boolean; error?: string } => {
  if (!name || typeof name !== 'string') return { valid: false, error: `${label} é obrigatório` };
  const trimmed = name.trim();
  if (trimmed.length < 2) return { valid: false, error: `${label} deve ter pelo menos 2 caracteres` };
  if (trimmed.length > 100) return { valid: false, error: `${label} não pode ter mais de 100 caracteres` };
  return { valid: true };
};

export const validatePhone = (phone: string): { valid: boolean; error?: string } => {
  if (!phone || typeof phone !== 'string') return { valid: false, error: 'Telefone é obrigatório' };
  const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;
  if (!phoneRegex.test(phone.trim())) return { valid: false, error: 'Número de telefone inválido' };
  return { valid: true };
};

export const validateURL = (url: string): { valid: boolean; error?: string } => {
  if (!url || typeof url !== 'string') return { valid: false, error: 'URL é obrigatório' };
  try {
    const parsed = new URL(url.trim());
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return { valid: false, error: 'URL deve começar com http:// ou https://' };
    }
    return { valid: true };
  } catch {
    return { valid: false, error: 'URL inválido' };
  }
};

export interface PassengerValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export const validatePassengerData = (data: {
  given_name?: string;
  family_name?: string;
  email?: string;
  phone_number?: string;
  born_on?: string;
  gender?: string;
  title?: string;
}): PassengerValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.given_name || data.given_name.trim().length < 2) {
    errors.given_name = 'O nome próprio deve ter pelo menos 2 caracteres';
  }

  if (!data.family_name || data.family_name.trim().length < 2) {
    errors.family_name = 'O apelido deve ter pelo menos 2 caracteres';
  }

  if (data.email && !validateEmail(data.email).valid) {
    errors.email = 'O endereço de email não é válido';
  }

  if (data.phone_number) {
    const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;
    if (!phoneRegex.test(data.phone_number.trim())) {
      errors.phone_number =
        'O número de telefone não é válido (use formato internacional, ex: +244923557577)';
    }
  }

  if (data.born_on) {
    const date = new Date(data.born_on);
    const now = new Date();
    if (isNaN(date.getTime()) || date >= now) {
      errors.born_on = 'A data de nascimento não é válida';
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};